import { useCallback, useRef, useSyncExternalStore } from 'react'
import * as Y from 'yjs'
import { DEFAULT_FRAME_STYLE, Y_FRAMES, type Frame } from '@praesto/shared'

type YFrame = Y.Map<unknown>

const framesMap = (doc: Y.Doc): Y.Map<YFrame> => doc.getMap<YFrame>(Y_FRAMES)

function readFrame(id: string, map: YFrame): Frame | null {
  const name = map.get('name')
  if (typeof name !== 'string') return null
  return {
    id,
    name,
    x: Number(map.get('x') ?? 0),
    y: Number(map.get('y') ?? 0),
    w: Number(map.get('w') ?? 100),
    h: Number(map.get('h') ?? 100),
    order: Number(map.get('order') ?? 0),
    // Ältere Frames kennen die Gestaltungsfelder noch nicht.
    background: String(map.get('background') ?? DEFAULT_FRAME_STYLE.background),
    borderColor: String(map.get('borderColor') ?? DEFAULT_FRAME_STYLE.borderColor),
    borderWidth: Number(map.get('borderWidth') ?? DEFAULT_FRAME_STYLE.borderWidth),
    // Ebenso Satzspiegel und Raster: Ohne Eintrag gilt „automatisch", also das,
    // was sich aus der Größe des Frames ergibt.
    padding: Number(map.get('padding') ?? DEFAULT_FRAME_STYLE.padding),
    columns: Number(map.get('columns') ?? DEFAULT_FRAME_STYLE.columns),
    rows: Number(map.get('rows') ?? DEFAULT_FRAME_STYLE.rows),
  }
}

/** Die Frames eines beliebigen Dokuments — auch eines aus dem Verlauf. */
export function framesOf(doc: Y.Doc): Frame[] {
  const result: Frame[] = []
  framesMap(doc).forEach((value, key) => {
    const frame = readFrame(key, value)
    if (frame) result.push(frame)
  })
  result.sort((a, b) => a.order - b.order)
  return result
}

/**
 * Alle Frames, aufsteigend nach `order` — das ist zugleich die
 * Präsentationsreihenfolge. Gleicher Cache-Mechanismus wie bei den Elementen:
 * `getSnapshot` darf bei unveränderten Daten kein neues Array liefern.
 */
export function useBoardFrames(doc: Y.Doc): Frame[] {
  const map = framesMap(doc)
  const cache = useRef<Frame[]>([])
  const stale = useRef(true)

  const subscribe = useCallback(
    (onChange: () => void) => {
      const handler = (): void => {
        stale.current = true
        onChange()
      }
      map.observeDeep(handler)
      return () => map.unobserveDeep(handler)
    },
    [map],
  )

  const getSnapshot = useCallback(() => {
    if (stale.current) {
      const result: Frame[] = []
      map.forEach((value, key) => {
        const frame = readFrame(key, value)
        if (frame) result.push(frame)
      })
      result.sort((a, b) => a.order - b.order)
      cache.current = result
      stale.current = false
    }
    return cache.current
  }, [map])

  return useSyncExternalStore(subscribe, getSnapshot)
}

export function nextFrameOrder(doc: Y.Doc): number {
  let max = 0
  framesMap(doc).forEach((frame) => {
    max = Math.max(max, Number(frame.get('order') ?? 0))
  })
  return max + 1
}

export function addFrame(doc: Y.Doc, frame: Frame): void {
  const map = new Y.Map<unknown>()
  doc.transact(() => {
    for (const [key, value] of Object.entries(frame)) {
      if (key === 'id') continue
      map.set(key, value)
    }
    framesMap(doc).set(frame.id, map)
  })
}

export function updateFrame(doc: Y.Doc, id: string, patch: Partial<Frame>): void {
  const frame = framesMap(doc).get(id)
  if (!frame) return
  doc.transact(() => {
    for (const [key, value] of Object.entries(patch)) {
      if (key === 'id' || value === undefined) continue
      frame.set(key, value)
    }
  })
}

export function removeFrame(doc: Y.Doc, id: string): void {
  doc.transact(() => framesMap(doc).delete(id))
}

/**
 * Verschiebt einen Frame in der Reihenfolge um eine Position.
 *
 * Die `order`-Werte werden anschließend neu durchnummeriert. Das ist bei einer
 * Handvoll Frames unproblematisch und erspart uns Bruchzahlen-Ordnungsschlüssel,
 * die bei gleichzeitigem Umsortieren durch zwei Personen ohnehin nur eine
 * andere Art von Konflikt erzeugen würden.
 */
export function moveFrame(doc: Y.Doc, ordered: readonly Frame[], id: string, delta: number): void {
  const index = ordered.findIndex((frame) => frame.id === id)
  const target = index + delta
  if (index < 0 || target < 0 || target >= ordered.length) return

  const next = [...ordered]
  const [moved] = next.splice(index, 1)
  if (!moved) return
  next.splice(target, 0, moved)

  doc.transact(() => {
    const map = framesMap(doc)
    next.forEach((frame, position) => {
      map.get(frame.id)?.set('order', position + 1)
    })
  })
}

/**
 * Schiebt einen Frame an die Stelle eines anderen — die Reihenfolge fürs Ziehen
 * in der Frame-Liste.
 *
 * Wie beim Umsortieren von Elementen wird danach die ganze Liste neu
 * durchnummeriert: Lücken oder doppelte Ordnungszahlen wären für alle anderen am
 * Board sofort sichtbar, weil daran die Präsentationsreihenfolge hängt.
 */
export function reorderFrame(
  doc: Y.Doc,
  ordered: readonly Frame[],
  id: string,
  beforeId: string,
): void {
  const from = ordered.findIndex((frame) => frame.id === id)
  const to = ordered.findIndex((frame) => frame.id === beforeId)
  if (from < 0 || to < 0 || from === to) return

  const next = [...ordered]
  const [moved] = next.splice(from, 1)
  if (!moved) return
  next.splice(to, 0, moved)

  doc.transact(() => {
    const map = framesMap(doc)
    next.forEach((frame, position) => {
      map.get(frame.id)?.set('order', position + 1)
    })
  })
}

export function createFrameId(): string {
  return crypto.randomUUID()
}
